import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getAuthStorage } from '../context/AuthContext';

// Base URL from environment or default to local development
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthStorage().getItem('disneyShopperToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    // Handle authentication errors
    if (response && response.status === 401) {
      localStorage.removeItem('disneyShopperToken');
      localStorage.removeItem('disneyShopperUser');
      sessionStorage.removeItem('disneyShopperToken');
      sessionStorage.removeItem('disneyShopperUser');

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Report unexpected server errors (5xx) to Sentry
    if (response?.status >= 500) {
      Sentry.captureException(error, {
        extra: {
          endpoint: error.config?.url,
          status: response?.status,
          serverMessage: response?.data?.error,
          page: window.location.pathname,
        },
      });
    }

    return Promise.reject(error);
  }
);

// Export API service methods - Updated with inventory management
const apiService = {
  // Auth endpoints
  auth: {
    register: (userData) => api.post('/auth/register', userData),
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (resetToken, password, confirmPassword) =>
      api.put(`/auth/reset-password/${resetToken}`, { password, confirmPassword }),
    verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
    resendVerification: () => api.post('/auth/resend-verification'),
    resendVerificationPublic: (email) => api.post('/auth/resend-verification-public', { email }),
    updateProfile: (data) => api.put('/auth/update-profile', data),
    changePassword: (data) => api.put('/auth/change-password', data),
    deleteAccount: (password) => api.delete('/auth/delete-account', { data: { password } }),
    getAddresses: () => api.get('/auth/addresses'),
    addAddress: (data) => api.post('/auth/addresses', data),
    updateAddress: (addressId, data) => api.put(`/auth/addresses/${addressId}`, data),
    deleteAddress: (addressId) => api.delete(`/auth/addresses/${addressId}`),
    setPrimaryAddress: (addressId) => api.patch(`/auth/addresses/${addressId}/primary`),
    getPreferences: () => api.get('/auth/preferences'),
    updatePreferences: (data) => api.put('/auth/preferences', data),
  },
  
  // Products endpoints with preorder support
  products: {
    getAll: (params) => api.get('/products', { params }),
    getRecommendations: (params) => api.get('/products/recommendations', { params }),
    getFieldValues: () => api.get('/products/field-values'),
    getById: (id) => api.get(`/products/${id}`),
    getByCategory: (category, params) => {
      // Convert URL slug back to the DB category name for backend lookup
      const categoryMapping = {
        'pins':                'Pins',
        'lanyards':            'Lanyards',
        'ears':                'Ears',
        'loungefly':           'Loungefly',
        'bags-backpacks':    'Bags & Backpacks',
        'clothing-apparel':  'Clothing & Apparel',
        'jewellery':           'Jewellery',
        'accessories':         'Accessories',
        'plush':               'Plush',
        'homeware':            'Homeware',
        'drinkware':           'Drinkware',
        'toys-games':        'Toys & Games',
        'stationery-art':    'Stationery & Art',
        'buckets-sippers':   'Buckets & Sippers',
        'ornaments':           'Ornaments',
      };
      
      const formattedCategory = categoryMapping[category] || category;
      return api.get('/products', { params: { ...params, category: formattedCategory } });
    },
    getStock: (id) => api.get(`/products/${id}/stock`),
    // New preorder endpoints
    getPreorderStats: () => api.get('/products/preorders/stats'),
    getPreorderProducts: (params) => api.get('/products/preorders', { params }),
    // Admin only
    create: (productData) => api.post('/products', productData),
    update: (id, productData) => api.put(`/products/${id}`, productData) ,
    delete: (id) => api.delete(`/products/${id}`),
    createWithImage: (formData) => {
      return api.post('/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    
    // Fixed updateWithImage method
    updateWithImage: (id, formData) => {
      return api.put(`/products/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    recalculateStatus: (productId) => api.post(`admin/products/recalculate-status`),
  },
  
  // checkout endpoints
  checkout: {
    // NEW methods for multi-step checkout
    getShippingRates: (value) => 
      api.post('/checkout/shipping-rates', { value }),
    
    createSessionWithExactShipping: (session_id, selected_rate_id, couponCode = null, addressData = null, selected_rate_ids = null) =>
      api.post('/checkout/create-session', {
        session_id,
        selected_rate_id,
        selected_rate_ids,
        couponCode,
        // NEW: Include current address data
        ...(addressData && {
          current_shipping_address: addressData.shippingAddress,
          current_billing_address: addressData.billingAddress,
          billing_same_as_shipping: addressData.billingSameAsShipping
        })
      }),

    getSessionStatus: (sessionId) => api.get(`/checkout/session-status/${sessionId}`),
  },
  shipping: {
    getRates: (destination_address) => api.post('/shipping/rates', { destination_address }),
    validateRate: (rate_id, cache_key) => api.post('/shipping/validate-rate', { rate_id, cache_key }),
    testConnection: () => api.get('/shipping/test')
  },
  // Categories endpoints
  categories: {
    getAll: () => api.get('/categories'),
    getById: (id) => api.get(`/categories/${id}`),
    getProducts: (id) => api.get(`/categories/${id}/products`),
    // Admin only
    create: (categoryData) => api.post('/categories', categoryData),
    update: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
    delete: (id) => api.delete(`/categories/${id}`),
  },

  cart: {
    // Get user's cart
    getCart: async () => {
      return api.get('/cart');
    },

    // Add inventory item to cart
    addInventoryItem: async (inventoryItemId) => {
      return api.post('/cart', { inventoryItemId });
    },

    // Add product to cart (legacy for non-authenticated)
    addProduct: async (productId, quantity = 1, isPreorder = false) => {
      return api.post('/cart', { 
        productId, 
        quantity, 
        isPreorder 
      });
    },

    // Add preorder item to cart
    addPreorderItem: async (productId, size) => {
      return api.post('/cart/preorder', { productId, size });
    },

    // Remove item from cart
    removeItem: async (cartItemId) => {
      return api.delete(`/cart/${cartItemId}`);
    },

    // Clear entire cart
    clearCart: async () => {
      return api.delete('/cart');
    },

    // Add free pixie dust gift to cart
    addPixieDust: () => api.post('/cart/pixie-dust'),

    // Update cart item (legacy - for quantity updates)
    updateItem: async (cartItemId, updates) => {
      return api.put(`/cart/${cartItemId}`, updates);
    }
  },
  
  // Orders endpoints with preorder support
  orders: {
    create: (orderData) => api.post('/orders', orderData),
    getAll: () => api.get('/orders'),
    getById: (id) => api.get(`/orders/${id}`),
    // New preorder order methods
    getPreorderOrders: () => api.get('/orders/preorders'),
    updatePreorderStatus: (id, status) => api.put(`/orders/${id}/preorder-status`, { status }),
    // Admin only
    updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
    updateTracking: (id, trackingData) => api.put(`/orders/${id}/tracking`, trackingData),
    getAllForAdmin: (params) => api.get('/admin/orders', { params }),
    getOrderStats: () => api.get('/admin/orders/stats'),
    updateInventoryItemStatus: (inventoryItemId, status) =>
      api.put('/admin/orders/inventory-item-status', { inventoryItemId, status }),
    updateOrderItemStatus: (orderId, itemIndex, status, extraBody = {}) =>
      api.put(`/admin/orders/${orderId}/items/${itemIndex}/status`, { status, ...extraBody }),
    refundOrderItem: (orderId, itemIndex) =>
      api.post(`/admin/orders/${orderId}/items/${itemIndex}/refund`),
    getCollectList: () => api.get('/admin/orders/collect-list'),
    // Open orders
    getOpenOrder: () => api.get('/orders/open'),
    getPostingMonthSummary: () => api.get('/admin/orders/posting-month-summary'),
    closeOpenOrders: (forceAll = false, cutoffDate = null) =>
      api.post('/admin/orders/close-open', { forceAll, ...(cutoffDate && { cutoffDate }) }),
    syncEasyshipOrders: () => api.post('/admin/orders/sync-easyship'),
  },
  
  // Requests endpoints
  requests: {
    create: (requestData) => api.post('/requests', requestData),
    getAll: () => api.get('/requests'),
    getById: (id) => api.get(`/requests/${id}`),
    cancel: (id) => api.put(`/requests/${id}/cancel`),
    addActivity: (id, activityData) => api.post(`/requests/${id}/activity`, activityData),
    // Per-item actions
    addItemToCart: (reqId, itemId) => api.post(`/requests/${reqId}/items/${itemId}/add-to-cart`),
    acceptItem: (reqId, itemId, linkedProductId) => api.put(`/requests/${reqId}/items/${itemId}/accept`, { linkedProductId }),
    rejectItem: (reqId, itemId, reason) => api.put(`/requests/${reqId}/items/${itemId}/reject`, { reason }),
    // Admin
    getAllForAdmin: (params) => api.get('/admin/requests', { params }),
    getStats: () => api.get('/admin/requests/stats'),
  },
  
  // Admin endpoints with inventory management
  admin: {
    getDashboard: () => api.get('/admin/dashboard'),
    getAdminUsers: () => api.get('/admin/users?role=admin'),
    getAllUsers: (params) => api.get('/admin/users/all', { params }),
    updateFriendsAndFamily: (userId, isFriendsAndFamily) =>
      api.put(`/admin/users/${userId}/friends-and-family`, { isFriendsAndFamily }),
    updatePixieDust: (userId, hasPixieDust) =>
      api.put(`/admin/users/${userId}/pixie-dust`, { hasPixieDust }),
    // New preorder management endpoints
    getPreorderDashboard: () => api.get('/admin/preorders/dashboard'),
    getPreorderStats: () => api.get('/admin/preorders/stats'),
    updatePreorderLimits: (productId, limits) => 
      api.put(`/admin/products/${productId}/preorder-limits`, limits),
    bulkUpdatePreorderStatus: (productIds, status) => 
      api.put('/admin/preorders/bulk-update', { productIds, status }),
    getPreorderQueue: (params) => api.get('/admin/preorders/queue', { params }),
    processPreorderFulfillment: (orderId) =>
      api.post(`/admin/preorders/${orderId}/fulfill`),
    getProducts: (params) => api.get('/admin/products', { params }),
    // Subscription management
    getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
    getPendingFulfillments: (params) => api.get('/admin/subscriptions/fulfillments/pending', { params }),
    getEligibleInventory: (fulfillmentId) => api.get(`/admin/subscriptions/fulfillments/${fulfillmentId}/eligible`),
    fulfillSubscription: (fulfillmentId, data) => api.post(`/admin/subscriptions/fulfillments/${fulfillmentId}/fulfill`, data),
  },

  // Inventory management endpoints
  inventory: {
    // Get all inventory items with filtering
    getAll: (params) => api.get('/admin/inventory', { params }),

    // Get single inventory item
    getById: (id) => api.get(`/admin/inventory/${id}`),
    
    // Create new inventory item
    create: (inventoryData) => api.post('/admin/inventory', inventoryData),
    
    // Update inventory item
    update: (id, inventoryData) => api.put(`/admin/inventory/${id}`, inventoryData),
    
    // Mark item as sold
    markAsSold: (id, saleData) => api.put(`/admin/inventory/${id}/mark-sold`, saleData),
    
    // Delete inventory item
    delete: (id) => api.delete(`/admin/inventory/${id}`),
    
    // Get analytics
    getAnalytics: (params) => api.get('/admin/inventory/analytics', { params }),
    
    // Export data
    export: (params) => api.get('/admin/inventory/export', {
      params,
      responseType: 'blob' // For CSV download
    }),

    // Monthly tax backlog
    getTaxBacklog: (params) => api.get('/admin/inventory/tax-backlog', { params }),

    getAvailableItems: async (productId, filters = {}) => {
      const queryParams = new URLSearchParams(filters).toString();
      const url = queryParams ? 
        `/cart/available/${productId}?${queryParams}` : 
        `/cart/available/${productId}`;
      return api.get(url);
    },

    // Get specific inventory item details
    getInventoryItem: async (inventoryItemId) => {
      return api.get(`/inventory/${inventoryItemId}`);
    },

    // Check if inventory item is still available
    checkAvailability: async (inventoryItemId) => {
      return api.get(`/inventory/${inventoryItemId}/availability`);
    },
    
    // Helper methods for the UI
    getStatusOptions: () => ['available', 'reserved', 'sold', 'damaged', 'lost', 'returned'],
    getSourceOptions: () => ['vinted', 'disneyland_paris', 'other_reseller', 'direct_purchase'],
    getChannelOptions: () => ['website', 'vinted', 'marketplace', 'local', 'other'],
    getConditionOptions: () => ['new', 'like_new', 'good', 'fair', 'poor'],
    getAuthenticityOptions: () => ['guaranteed_authentic', 'likely_authentic', 'unknown'],
    
    // Format helpers
    formatSource: (source) => {
      const sourceLabels = {
        'vinted': 'Vinted',
        'disneyland_paris': 'Disneyland Paris',
        'other_reseller': 'Other Reseller',
        'direct_purchase': 'Direct Purchase'
      };
      return sourceLabels[source] || source;
    },
    
    formatCondition: (condition) => {
      const conditionLabels = {
        'new': 'New',
        'like_new': 'Like New',
        'good': 'Good',
        'fair': 'Fair',
        'poor': 'Poor'
      };
      return conditionLabels[condition] || condition;
    },
    
    formatChannel: (channel) => {
      const channelLabels = {
        'website': 'Website',
        'vinted': 'Vinted',
        'marketplace': 'Marketplace',
        'local': 'Local Sale',
        'other': 'Other'
      };
      return channelLabels[channel] || channel;
    }
  },

  // Subscription endpoints
  subscriptions: {
    getTiers: () => api.get('/subscriptions/tiers'),
    create: (data) => api.post('/subscriptions/create', data),
    getMy: () => api.get('/subscriptions/my'),
    pause: (id) => api.put(`/subscriptions/${id}/pause`),
    resume: (id) => api.put(`/subscriptions/${id}/resume`),
    cancel: (id) => api.put(`/subscriptions/${id}/cancel`),
    getBilling: (id) => api.get(`/subscriptions/${id}/billing`),
  },

  // Config endpoint
  config: {
      getPostingDate: () => api.get('/config/posting-date'),
  },

  // Settings endpoints
  settings: {
    getFeaturedLoot: () => api.get('/settings/featured-loot'),
    updateFeaturedLoot: (productIds) => api.put('/admin/settings/featured-loot', { productIds }),
    getMonthlyPinsCalendar: () => api.get('/settings/monthly-pins-calendar'),
    updateMonthlyPinsCalendar: (imageUrl) => api.put('/admin/settings/monthly-pins-calendar', { imageUrl }),
    getPostingDate: () => api.get('/settings/posting-date'),
    updatePostingDate: (postingDate, nextPostingDate, postingDate3, postingDate4) => api.put('/admin/settings/posting-date', { postingDate, nextPostingDate, postingDate3, postingDate4 }),
  },
  
  // Analytics endpoints (new)
  analytics: {
    getPreorderAnalytics: (params) => api.get('/analytics/preorders', { params }),
    getInventoryAnalytics: (params) => api.get('/analytics/inventory', { params }),
    getCustomerPreorderBehavior: (params) => api.get('/analytics/customer-preorder-behavior', { params }),
  },
  
  // Notifications endpoints (new)
  notifications: {
    getPreorderUpdates: () => api.get('/notifications/preorder-updates'),
    subscribeToPreorderNotifications: (productId, email) => 
      api.post('/notifications/preorder-subscribe', { productId, email }),
    unsubscribeFromPreorderNotifications: (productId, email) => 
      api.post('/notifications/preorder-unsubscribe', { productId, email }),
    sendPreorderRestockNotification: (productId) => 
      api.post(`/notifications/preorder-restock/${productId}`),
  },
  
  // Upload endpoints
  uploads: {
    uploadImage: async (file) => {
      // Try direct S3 presigned URL first (faster, bypasses Lambda)
      try {
        const presignedResponse = await api.post('/uploads/presigned-url', {
          contentType: file.type,
        });

        const { uploadUrl, fileUrl } = presignedResponse.data.data;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 PUT failed: ${uploadResponse.status}`);
        }

        return {
          data: {
            success: true,
            data: {
              fileName: fileUrl.split('/').pop(),
              filePath: fileUrl,
              originalName: file.name,
              size: file.size,
              mimetype: file.type,
            }
          }
        };
      } catch (error) {
        // Fall back to server-side upload through Lambda (no CORS constraints)
        const formData = new FormData();
        formData.append('image', file);
        formData.append('fallbackReason', JSON.stringify({
          error: error.message,
          errorName: error.name,
          fileType: file.type,
          fileSize: file.size,
          online: navigator.onLine,
          userAgent: navigator.userAgent,
        }));
        return api.post('/uploads/images', formData, {
          headers: { 'Content-Type': undefined },
        });
      }
    },

    deleteImage: (imageUrl) => {
      return api.delete('/uploads/images', { data: { imageUrl } });
    },
    deleteImages: (imageUrls) => {
      return api.delete('/uploads/images/bulk', { data: { imageUrls } });
    },
  },

  // Utility methods for preorder management
  preorder: {
    // Check if preorder is currently active for a product
    checkPreorderStatus: (product) => {
      if (!product.isPreorderAvailable) return 'not_available';
      
      const now = new Date();
      
      if (product.preorderStartDate && now < new Date(product.preorderStartDate)) {
        return 'upcoming';
      }
      
      if (product.preorderEndDate && now > new Date(product.preorderEndDate)) {
        return 'ended';
      }
      
      if (product.preorderQuantityLimit && 
          product.currentPreorderCount >= product.preorderQuantityLimit) {
        return 'full';
      }
      
      return 'available';
    },
    
    // Calculate available preorder slots
    getAvailableSlots: (product) => {
      if (!product.isPreorderAvailable || !product.preorderQuantityLimit) {
        return 0;
      }
      return Math.max(0, product.preorderQuantityLimit - product.currentPreorderCount);
    },
    
    // Format preorder delivery date
    formatDeliveryDate: (dateString) => {
      if (!dateString) return null;
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    
    // Validate preorder request
    validatePreorderRequest: (product, quantity) => {
      const status = apiService.preorder.checkPreorderStatus(product);
      
      if (status !== 'available') {
        return { valid: false, error: `Preorder is ${status}` };
      }
      
      const availableSlots = apiService.preorder.getAvailableSlots(product);
      
      if (quantity > availableSlots) {
        return { 
          valid: false, 
          error: `Only ${availableSlots} preorder slots available` 
        };
      }
      
      return { valid: true };
    }
  }
};

export default apiService;